import { differenceInYears, format } from 'date-fns';
import type { IntlShape } from 'react-intl';
import type { PatientsListQueryParams } from '~/backend/connector-next-api/connector-next-components';
import type {
    PatientDetailDto,
    PatientSummaryDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import type { CourseStageColorKey } from '~/theme/theme';
import { parseWireDate } from '~/utils/date';
import { cutUID } from '~/utils/string';

/** connector-next CourseStage wire values, from the generated client. */
type CourseStageWire = NonNullable<
    PatientsListQueryParams['courseStage']
>[number];

/**
 * connector-next sort keys the list can request. Only the columns the API can
 * sort server-side are offered (Patient, Status, Last visit); each column owns
 * its own header label.
 */
export const SORT_COLUMNS = ['name', 'courseStage', 'lastActivity'] as const;

export type SortValue = (typeof SORT_COLUMNS)[number];

export type SortDirection = 'asc' | 'desc';
export type Sort = { key: SortValue; direction: SortDirection };

/**
 * The direction a column adopts when it is first selected: names read A–Z,
 * the stage runs the course forward (Received first), dates newest first. This
 * mirrors the order connector-next applies when a request omits the direction
 * parameter.
 */
const NATURAL_SORT_DIRECTION: Record<SortValue, SortDirection> = {
    name: 'asc',
    courseStage: 'asc',
    lastActivity: 'desc',
};

export const DEFAULT_SORT: Sort = { key: 'lastActivity', direction: 'desc' };

/**
 * Sort after clicking a column header: a new column adopts its natural
 * direction, the active column flips direction.
 */
export function nextSort(current: Sort, key: SortValue): Sort {
    if (current.key !== key) {
        return { key, direction: NATURAL_SORT_DIRECTION[key] };
    }
    return {
        key,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
}

type SortParam = NonNullable<PatientsListQueryParams['sort']>;
type DirectionParam = NonNullable<PatientsListQueryParams['direction']>;

const SORT_KEY_WIRE: Record<SortValue, SortParam> = {
    name: 'Name',
    courseStage: 'CourseStage',
    lastActivity: 'LastActivity',
};

/** Wire value for the connector-next `sort` query parameter. */
export function sortKeyParam(key: SortValue): SortParam {
    return SORT_KEY_WIRE[key];
}

/**
 * Wire value for the connector-next `direction` query parameter. Sorting is
 * entirely server-side: every request states its direction rather than
 * relying on the API's per-key default, so the header and the returned page
 * cannot disagree.
 */
export function sortDirectionParam(direction: SortDirection): DirectionParam {
    return direction === 'asc' ? 'Ascending' : 'Descending';
}

/**
 * Course order of the stages; each stage's dot color lives in the theme
 * (`palette.courseStage`) and is resolved by the component.
 */
const COURSE_STAGE_META = {
    Received: {
        labelId: 'patientList.stage.received',
        shortLabelId: 'patientList.stage.short.received',
        colorKey: 'received',
    },
    ImagePreparation: {
        labelId: 'patientList.stage.imagePreparation',
        shortLabelId: 'patientList.stage.short.imagePreparation',
        colorKey: 'imagePreparation',
    },
    ImageRegistration: {
        labelId: 'patientList.stage.imageRegistration',
        shortLabelId: 'patientList.stage.short.imageRegistration',
        colorKey: 'imageRegistration',
    },
    OarContouring: {
        labelId: 'patientList.stage.oarContouring',
        shortLabelId: 'patientList.stage.short.oarContouring',
        colorKey: 'oarContouring',
    },
    TargetContouring: {
        labelId: 'patientList.stage.targetContouring',
        shortLabelId: 'patientList.stage.short.targetContouring',
        colorKey: 'targetContouring',
    },
    DosePrediction: {
        labelId: 'patientList.stage.dosePrediction',
        shortLabelId: 'patientList.stage.short.dosePrediction',
        colorKey: 'dosePrediction',
    },
    Review: {
        labelId: 'patientList.stage.review',
        shortLabelId: 'patientList.stage.short.review',
        colorKey: 'review',
    },
    Exported: {
        labelId: 'patientList.stage.exported',
        shortLabelId: 'patientList.stage.short.exported',
        colorKey: 'exported',
    },
    // Keyed by the generated wire union, so a connector enum change (added,
    // renamed or removed member) fails compilation here instead of silently
    // desynchronising the filter menu and the Stage column.
} as const satisfies Record<
    CourseStageWire,
    { labelId: string; shortLabelId: string; colorKey: CourseStageColorKey }
>;

export type CourseStage = keyof typeof COURSE_STAGE_META;

/**
 * The course-stage wire values, in course order. Sent to the connector verbatim, so this is also
 * the order the filter menu offers them in.
 */
export const COURSE_STAGE_VALUES = Object.keys(
    COURSE_STAGE_META
) as readonly CourseStage[];

/** Theme palette key colouring a course stage in the list. */
export function courseStageColorKey(stage: CourseStage): CourseStageColorKey {
    return COURSE_STAGE_META[stage].colorKey;
}

/** Short label message id of a course stage, for where its full name does not fit. */
export function courseStageShortLabelId(
    stage: CourseStage
): (typeof COURSE_STAGE_META)[CourseStage]['shortLabelId'] {
    return COURSE_STAGE_META[stage].shortLabelId;
}

/** Label message id of a course stage, from the same table that colors it in the list. */
export function courseStageLabelId(
    stage: CourseStage
): (typeof COURSE_STAGE_META)[CourseStage]['labelId'] {
    return COURSE_STAGE_META[stage].labelId;
}

/**
 * Meta for a CourseStage wire string, or undefined when the value is not one.
 * Only patient-level stages belong here: a dataset's `status` is a SyncStatus
 * (Pending/Downloading/Downloaded/Error) and shares no member with this table.
 */
function courseStageMeta(
    stage: string | null | undefined
): { labelId: string; colorKey: CourseStageColorKey } | undefined {
    return wireLookup(COURSE_STAGE_META, stage);
}

/**
 * The entry a wire string names in a table keyed by wire values, or undefined
 * when it names none. Only the table's own keys count: a value from another
 * vocabulary that happens to share a spelling must not resolve.
 */
export function wireLookup<T extends Record<string, unknown>>(
    table: T,
    key: string | null | undefined
): T[keyof T] | undefined {
    return key && Object.hasOwn(table, key) ? table[key as keyof T] : undefined;
}

/** Dot tone of a dataset's retrieval state; resolved to a color at the UI edge. */
export type SyncStatusTone = 'pending' | 'downloading' | 'downloaded' | 'error';

/** Data-retrieval lifecycle of a dataset (DICOM SyncStatus). */
const SYNC_STATUS_META = {
    Pending: {
        labelId: 'patientList.dataset.syncStatus.pending',
        tone: 'pending',
    },
    Downloading: {
        labelId: 'patientList.dataset.syncStatus.downloading',
        tone: 'downloading',
    },
    Downloaded: {
        labelId: 'patientList.dataset.syncStatus.downloaded',
        tone: 'downloaded',
    },
    Error: {
        labelId: 'patientList.dataset.syncStatus.error',
        tone: 'error',
    },
} as const satisfies Record<string, { labelId: string; tone: SyncStatusTone }>;

/** Label and dot tone for a dataset's SyncStatus, or undefined when it carries none. */
export function datasetSyncStatusMeta(
    intl: IntlShape,
    status: string | null | undefined
): { label: string; tone: SyncStatusTone } | undefined {
    const meta = wireLookup(SYNC_STATUS_META, status);
    return (
        meta && {
            label: intl.formatMessage({ id: meta.labelId }),
            tone: meta.tone,
        }
    );
}

/**
 * Unknown/missing stages display as Received, which is also where a course
 * starts — there is no derived value to fall back to and none is invented.
 */
export function patientCourseStageMeta(
    intl: IntlShape,
    stage: string | null | undefined
): {
    label: string;
    colorKey: CourseStageColorKey;
} {
    const meta = courseStageMeta(stage) ?? COURSE_STAGE_META.Received;
    return {
        label: intl.formatMessage({ id: meta.labelId }),
        colorKey: meta.colorKey,
    };
}

/** The wire name of a course stage, or Received when it is unknown or absent. */
export function resolveCourseStage(
    stage: string | null | undefined
): CourseStage {
    return stage && Object.hasOwn(COURSE_STAGE_META, stage)
        ? (stage as CourseStage)
        : 'Received';
}

/**
 * The stages a patient has marked done, in course order, with anything the
 * table does not know dropped. A stage nobody can name cannot be drawn, and
 * silently drawing it as some other stage would be worse than omitting it.
 */
export function patientCourseStagesDone(
    done: readonly string[] | null | undefined
): readonly CourseStage[] {
    const marked = new Set(done ?? []);
    return COURSE_STAGE_VALUES.filter((stage) => marked.has(stage));
}

/**
 * Whole years between an ISO yyyy-MM-dd birth date and `at`; undefined if
 * unknown or not yet reached.
 *
 * `~/utils/date` calculateAge covers the DICOM yyyyMMdd form against the
 * current date; this wire field is a DateOnly and `at` is injected so the
 * result stays testable.
 */
export function ageInYears(
    birthDate: string | null | undefined,
    at: Date
): number | undefined {
    const born = parseWireDate(birthDate);
    if (!born) {
        return undefined;
    }
    const age = differenceInYears(at, born);
    return age >= 0 ? age : undefined;
}

/**
 * Patient ID for display. A DICOM PatientID can be up to 64 chars; long ones
 * are middle-truncated so the ID can never crowd out the patient name.
 */
export function formatPatientId(id: string | null | undefined): string {
    if (!id) {
        return '-';
    }
    return id.length > 15 ? (cutUID(id, 6, 6) ?? id) : id;
}

/** "Kasper Tontti" → "KT"; single-word names use their first letter. */
export function initialsOf(name: string): string {
    return name
        .split(/[\s,]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
}

/**
 * DICOM Person Name (family^given^middle^prefix^suffix; groups split by '=')
 * rendered as "Family, Prefix Given Middle Suffix" from the alphabetic group
 * only. Values without PN separators pass through unchanged.
 */
export function formatPatientName(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    const alphabetic = value.split('=')[0] ?? '';
    if (!alphabetic.includes('^')) {
        return alphabetic.trim();
    }
    const [family, given, middle, prefix, suffix] = alphabetic
        .split('^')
        .map((part) => part.trim());
    const rest = [prefix, given, middle, suffix].filter(Boolean).join(' ');
    if (family && rest) {
        return `${family}, ${rest}`;
    }
    return family || rest;
}

/** yyyy-MM-dd calendar day of a wire value, or '' for missing/invalid. */
export function formatDay(value: string | null | undefined): string {
    const parsed = parseWireDate(value);
    return parsed ? format(parsed, 'yyyy-MM-dd') : '';
}

/**
 * Localized display form ("Feb 18, 2026") of the same day as formatDay, or ''
 * for missing/invalid. Formatted through the active react-intl locale so month
 * names follow the patient list's language rather than defaulting to English.
 */
export function formatDayText(
    intl: IntlShape,
    value: string | null | undefined
): string {
    const parsed = parseWireDate(value);
    return parsed
        ? intl.formatDate(parsed, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
          })
        : '';
}

const SEX_MESSAGE_IDS: Record<string, string> = {
    M: 'sex.male',
    F: 'sex.female',
    O: 'sex.other',
};

/** Localized label for DICOM sex M/F/O; other values pass through. */
export function sexLabel(
    intl: IntlShape,
    sex: string | null | undefined
): string {
    if (!sex) {
        return '';
    }
    const messageId = SEX_MESSAGE_IDS[sex];
    return messageId !== undefined
        ? intl.formatMessage({ id: messageId })
        : sex;
}

/** "Male 74y" / "Female" / "74y" — whichever parts are known. */
export function sexAgeText(
    intl: IntlShape,
    sex: string | null | undefined,
    age: number | undefined
): string {
    return [
        sexLabel(intl, sex),
        age !== undefined
            ? intl.formatMessage({ id: 'patientList.ageShort' }, { age })
            : undefined,
    ]
        .filter(Boolean)
        .join(' ');
}

/** What a dataset is, as far as the list needs to tell them apart. */
export type DatasetKind =
    | 'image'
    | 'structureSet'
    | 'dose'
    | 'plan'
    | 'registration'
    | 'other';

/**
 * The RT object kinds: wire datasetType, the DICOM modality the UI displays,
 * and the owning count on a patient summary.
 */
const RT_OBJECTS = [
    {
        kind: 'structureSet',
        datasetType: 'StructureSet',
        modality: 'RTSTRUCT',
        count: (patient: PatientSummaryDto) => patient.structureSetCount,
    },
    {
        kind: 'dose',
        datasetType: 'Dose',
        modality: 'RTDOSE',
        count: (patient: PatientSummaryDto) => patient.doseCount,
    },
    {
        kind: 'plan',
        datasetType: 'Plan',
        modality: 'RTPLAN',
        count: (patient: PatientSummaryDto) => patient.planCount,
    },
    {
        kind: 'registration',
        datasetType: 'Registration',
        modality: 'REG',
        count: (patient: PatientSummaryDto) => patient.registrationCount,
    },
] as const satisfies ReadonlyArray<{
    kind: DatasetKind;
    datasetType: string;
    modality: string;
    count: (patient: PatientSummaryDto) => number | undefined;
}>;

/** All series of a patient detail, flattened across studies. */
export function flattenSeries(
    detail: PatientDetailDto | undefined
): SeriesDto[] {
    return (detail?.studies ?? []).flatMap((study) => study.series ?? []);
}

/**
 * RT object series arrive with a wire `datasetType` and no `modality`;
 * displayed under their DICOM modality names so the panel matches the
 * list's RT chips.
 */
const DATASET_TYPE_MODALITY: Record<string, string> = Object.fromEntries(
    RT_OBJECTS.map((rtObject) => [rtObject.datasetType, rtObject.modality])
);

/**
 * Whether a series is an image rather than an RT object.
 *
 * A denylist, deliberately: the four RT kinds are the closed set the wire
 * names, and a series whose `datasetType` the backend did not set is still an
 * image series. An allowlist would silently drop those.
 */
export function isImageSeries(series: SeriesDto): boolean {
    return (
        series.datasetType !== 'StructureSet' &&
        series.datasetType !== 'Registration' &&
        series.datasetType !== 'Dose' &&
        series.datasetType !== 'Plan'
    );
}

/**
 * Which primary designation a series can hold, or null when it can hold
 * neither.
 *
 * A patient designates one planning image and one structure set. Doses, plans
 * and registrations are not designated: they are derived from, or describe a
 * relation between, the two that are.
 */
export function primaryDatasetKind(
    series: SeriesDto
): 'image' | 'structureSet' | null {
    if (series.datasetType === 'Image3D') {
        return 'image';
    }
    if (series.datasetType === 'StructureSet') {
        return 'structureSet';
    }
    return null;
}

/**
 * The image series a structure set was contoured on, or undefined when it
 * names none or names one that is not among `series`.
 *
 * One lookup behind every question about that link — which image a set nests
 * under, which image an open should show it on, and which image a retrieval
 * has to bring with it — so none of them can answer differently.
 */
export function imageContouredOn(
    series: readonly SeriesDto[],
    structureSet: SeriesDto
): SeriesDto | undefined {
    const referenced = structureSet.structureSet?.referencedImageSeriesUid;
    if (!referenced) {
        return undefined;
    }
    return series.find(
        (s) =>
            s.datasetType !== 'StructureSet' &&
            s.seriesInstanceUid === referenced
    );
}

const RT_OBJECT_KIND_BY_MODALITY: ReadonlyMap<string, DatasetKind> = new Map(
    RT_OBJECTS.map((rtObject) => [rtObject.modality, rtObject.kind])
);

/**
 * Whether a DICOM modality names an RT object rather than an image series.
 *
 * An RT object is stored as one object and carries no instance tally, so a count of what has
 * arrived for it cannot be formed. An image series is the only kind counted instance by instance.
 */
export function isRtObjectModality(modality: string | undefined): boolean {
    return modality !== undefined && RT_OBJECT_KIND_BY_MODALITY.has(modality);
}

/** Modality label with no modality behind it: a series that named neither a modality nor a type. */
const UNKNOWN_MODALITY = '?';

/**
 * The kind of dataset a displayed modality label stands for: an RT object's
 * own kind, `other` for the unknown-modality placeholder, and an image series
 * for every real modality (including the connector's `Unknown`, which is an
 * image whose modality did not parse).
 */
export function modalityKind(modality: string): DatasetKind {
    if (modality === UNKNOWN_MODALITY) {
        return 'other';
    }
    return RT_OBJECT_KIND_BY_MODALITY.get(modality) ?? 'image';
}

/** The kind of dataset a series is, from the modality label it displays under. */
export function seriesKind(series: SeriesDto): DatasetKind {
    return modalityKind(seriesModality(series));
}

/** Modality label of a series with the wire fallbacks the panel displays. */
export function seriesModality(series: SeriesDto): string {
    if (series.modality) {
        return series.modality;
    }
    if (series.datasetType) {
        return DATASET_TYPE_MODALITY[series.datasetType] ?? series.datasetType;
    }
    return UNKNOWN_MODALITY;
}

/** RT object labels of a patient row: owned kinds only, in RT_OBJECTS order. */
export function patientRtObjects(patient: PatientSummaryDto): string[] {
    return RT_OBJECTS.filter(
        (rtObject) => (rtObject.count(patient) ?? 0) > 0
    ).map((rtObject) => rtObject.modality);
}

/** Image-modality chips of a patient row: owned modalities only, name-sorted. */
export function patientImageModalities(
    patient: PatientSummaryDto
): Array<[string, number]> {
    return Object.entries(patient.imageModalityCounts ?? {})
        .filter(([, count]) => count > 0)
        .sort(([a], [b]) => a.localeCompare(b));
}

/** Group label for series without a usable date; sorts after all real days. */
export const UNKNOWN_DAY = '—';

/**
 * One row of the dataset list: a series, with the structure sets contoured on
 * it beneath. `structureSets` is empty for everything that is not an image
 * with sets, which is most rows.
 */
export type SeriesNode = {
    series: SeriesDto;
    structureSets: SeriesDto[];
    /**
     * A structure set that names the image it was contoured on, where that
     * image is not among the patient's series. Distinguishes "we have the
     * image and nested it" from "the image is elsewhere" from "the set names
     * no image at all", so the row can say which.
     */
    referencedImageMissing?: boolean;
    /**
     * The image itself is filtered out of view while structure sets drawn on
     * it are shown. The sets stay nested — they are still openable on it —
     * and the row names the image instead of showing it.
     */
    seriesHidden?: boolean;
};

export type SeriesDayGroup = {
    /** yyyy-MM-dd grouping key, or the unknown-day dash for undated series. */
    day: string;
    /** Display form of `day` ("Feb 18 2026"), or the dash for undated groups. */
    label: string;
    /** Distinct modality labels held that day, nested sets included, name-sorted. */
    modalities: string[];
    /** The day's rows. A nested structure set is not one of these. */
    nodes: SeriesNode[];
    /** Series the day accounts for, nested sets included. */
    seriesCount: number;
};

/** Every series as its own row, nesting nothing. */
export function flatSeriesNodes(series: readonly SeriesDto[]): SeriesNode[] {
    return series.map((s) => ({ series: s, structureSets: [] }));
}

/**
 * Attaches each structure set to the image it was contoured on, by the
 * `ReferencedFrameOfReference` link the connector extracts on import.
 *
 * A set whose image is not among these series stays a row of its own rather
 * than disappearing: the drilldown lists what the patient has, and a set with
 * nowhere to nest is still one of those things. It is also why nesting is
 * decided over the whole list before the days are formed — a set is regularly
 * dated days after the image it belongs to, and following its image is what
 * makes the tree readable.
 */
export function nestStructureSetsUnderImages(
    series: readonly SeriesDto[]
): SeriesNode[] {
    const nodes = new Map<SeriesDto, SeriesNode>();
    const byImageSeriesUid = new Map<string, SeriesNode>();
    const nested = new Set<SeriesDto>();

    for (const s of series) {
        const node: SeriesNode = { series: s, structureSets: [] };
        nodes.set(s, node);
        // Only an image can hold sets; an RT object carries a series UID too.
        if (s.datasetType !== 'StructureSet' && s.seriesInstanceUid) {
            byImageSeriesUid.set(s.seriesInstanceUid, node);
        }
    }

    for (const s of series) {
        const referenced = s.structureSet?.referencedImageSeriesUid;
        if (!referenced) {
            continue;
        }
        const image = byImageSeriesUid.get(referenced);
        if (image !== undefined && image.series !== s) {
            image.structureSets.push(s);
            nested.add(s);
        } else if (image === undefined) {
            nodes.get(s)!.referencedImageMissing = true;
        }
    }

    return series.filter((s) => !nested.has(s)).map((s) => nodes.get(s)!);
}

/** Orders series newest first; stable for equal or missing times. */
function newestSeriesFirst(a: SeriesDto, b: SeriesDto): number {
    // ISO wire datetimes compare as strings.
    return (b.seriesDateTime ?? '').localeCompare(a.seriesDateTime ?? '');
}

/**
 * Groups rows by calendar day of `seriesDateTime`, newest day first and newest
 * row first within a day; rows without a date collect under the unknown-day
 * dash at the end. A row's date is its own series' date, so structure sets
 * nested under an image follow the image's acquisition, not their own; among
 * themselves, the sets under an image are also newest first.
 */
function groupNodesByDay(
    intl: IntlShape,
    nodes: readonly SeriesNode[]
): SeriesDayGroup[] {
    const byDay = new Map<string, SeriesNode[]>();
    const newestFirst = [...nodes]
        .sort((a, b) => newestSeriesFirst(a.series, b.series))
        .map((node) => ({
            ...node,
            structureSets: [...node.structureSets].sort(newestSeriesFirst),
        }));
    for (const node of newestFirst) {
        const day = formatDay(node.series.seriesDateTime) || UNKNOWN_DAY;
        const bucket = byDay.get(day);
        if (bucket) {
            bucket.push(node);
        } else {
            byDay.set(day, [node]);
        }
    }
    const days = [...byDay.keys()].sort((a, b) => {
        if (a === UNKNOWN_DAY) {
            return 1;
        }
        if (b === UNKNOWN_DAY) {
            return -1;
        }
        return b.localeCompare(a);
    });
    return days.map((day) => {
        const grouped = byDay.get(day) ?? [];
        const modalities = [
            ...new Set(
                grouped
                    .flatMap((node) => [node.series, ...node.structureSets])
                    .map(seriesModality)
            ),
        ].sort((a, b) => a.localeCompare(b));
        return {
            day,
            label: day === UNKNOWN_DAY ? UNKNOWN_DAY : formatDayText(intl, day),
            modalities,
            nodes: grouped,
            seriesCount: grouped.reduce(
                (total, node) => total + 1 + node.structureSets.length,
                0
            ),
        };
    });
}

/** Day groups of flat rows: every series stands on its own. */
export function groupSeriesByDay(
    intl: IntlShape,
    series: readonly SeriesDto[]
): SeriesDayGroup[] {
    return groupNodesByDay(intl, flatSeriesNodes(series));
}

/**
 * Day groups of the image tree: each set sits under the image it was drawn on,
 * and the days run newest acquisition first. The primary structure set is
 * listed first under its image; the primary image keeps its place in time.
 */
export function groupSeriesTreeByDay(
    intl: IntlShape,
    series: readonly SeriesDto[],
    primaryStructureSetId?: string | null
): SeriesDayGroup[] {
    return hoistPrimaryStructureSet(
        groupNodesByDay(intl, nestStructureSetsUnderImages(series)),
        primaryStructureSetId
    );
}

/**
 * The day groups narrowed to the given modalities; an empty set narrows
 * nothing. A structure set that matches keeps its place under its image even
 * when the image does not match, so it stays openable on it; a day left with
 * nothing is dropped. Counts and modality lists describe what is shown.
 */
export function filterDayGroupsByModality(
    groups: readonly SeriesDayGroup[],
    modalities: ReadonlySet<string>
): SeriesDayGroup[] {
    if (modalities.size === 0) {
        return [...groups];
    }
    const matches = (series: SeriesDto) =>
        modalities.has(seriesModality(series));
    return groups.flatMap((group) => {
        const nodes = group.nodes.flatMap((node): SeriesNode[] => {
            const structureSets = node.structureSets.filter(matches);
            if (matches(node.series)) {
                return [{ ...node, structureSets }];
            }
            return structureSets.length > 0
                ? [{ ...node, structureSets, seriesHidden: true }]
                : [];
        });
        if (nodes.length === 0) {
            return [];
        }
        const shown = shownSeries(nodes);
        return [
            {
                ...group,
                nodes,
                seriesCount: shown.length,
                modalities: [...new Set(shown.map(seriesModality))].sort(
                    (a, b) => a.localeCompare(b)
                ),
            },
        ];
    });
}

/** Every series a list of day groups shows, nested structure sets included, in display order. */
export function shownSeriesOf(groups: readonly SeriesDayGroup[]): SeriesDto[] {
    return groups.flatMap((group) => shownSeries(group.nodes));
}

function shownSeries(nodes: readonly SeriesNode[]): SeriesDto[] {
    return nodes.flatMap((node) => [
        ...(node.seriesHidden ? [] : [node.series]),
        ...node.structureSets,
    ]);
}

/**
 * Moves the primary structure set to the front of the sets nested under its
 * image, so the designated pair reads top to bottom: the image first, and
 * under it the set that was chosen with it.
 *
 * Applied to every node rather than only the primary image's. The pair is
 * kept consistent by the aggregate — a designated set's image IS the primary
 * image — but the panel renders what the server sent, and a set that is
 * somewhere else in a snapshot taken mid-change still belongs at the top of
 * whichever image holds it.
 */
function hoistPrimaryStructureSet(
    groups: readonly SeriesDayGroup[],
    primaryStructureSetId: string | null | undefined
): SeriesDayGroup[] {
    if (!primaryStructureSetId) {
        return [...groups];
    }
    return groups.map((group) => ({
        ...group,
        nodes: group.nodes.map((node) =>
            node.structureSets.some((set) => set.id === primaryStructureSetId)
                ? {
                      ...node,
                      structureSets: [
                          ...node.structureSets.filter(
                              (set) => set.id === primaryStructureSetId
                          ),
                          ...node.structureSets.filter(
                              (set) => set.id !== primaryStructureSetId
                          ),
                      ],
                  }
                : node
        ),
    }));
}

/**
 * Along-axis geometry of an image series, from the slice positions the
 * connector lists. Domain units (mm); the UI converts at its boundary.
 */
export type SliceGeometry = {
    /** Distance between neighbouring slices when it is the same throughout; undefined when uneven. */
    spacingMm: number | undefined;
    /** Distance from the first slice position to the last. */
    extentMm: number;
};

/**
 * Spacing between slices and the extent they cover, or undefined below two
 * positioned slices. Spacing is stated only while every gap agrees within the
 * tolerance the connector applies when it validates a volume (0.1 mm or 1 %
 * of the mean gap); otherwise it is left unstated rather than averaged, since
 * an averaged spacing would hide a gap or a duplicate.
 *
 * The connector records 0 for a slice that carries neither a slice location
 * nor a position, so a series whose positions all read 0 has no geometry
 * on record; nothing is stated for it rather than a spacing warning and an
 * extent of nothing.
 */
export function sliceGeometry(series: SeriesDto): SliceGeometry | undefined {
    const positions = (series.slices ?? [])
        .map((slice) => slice.sliceLocationMm)
        .filter((z): z is number => typeof z === 'number' && Number.isFinite(z))
        .sort((a, b) => a - b);
    if (positions.length < 2 || positions.every((z) => z === 0)) {
        return undefined;
    }
    const gaps = positions.slice(1).map((z, index) => z - positions[index]);
    const meanGap = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
    const tolerance = Math.max(0.1, meanGap * 0.01);
    const uniform = gaps.every((gap) => Math.abs(gap - meanGap) <= tolerance);
    return {
        spacingMm: uniform && meanGap > 0 ? meanGap : undefined,
        extentMm: positions[positions.length - 1] - positions[0],
    };
}

/** "Bladder · Rectum · … +N" preview of ROI names, or undefined when absent. */
export function roiNamesPreview(series: SeriesDto): string | undefined {
    const names = (series.structureSet?.structures ?? [])
        .map((structure) => structure.roiName)
        .filter((name): name is string => !!name);
    if (names.length === 0) {
        return undefined;
    }
    const head = names.slice(0, 4).join(', ');
    return names.length > 4 ? `${head} +${names.length - 4}` : head;
}

/**
 * CSS colour of a structure's DICOM ROI Display Color, which the connector
 * stores as the backslash-joined `r\g\b` string of the attribute; undefined
 * when absent or not three 0–255 integers, so a malformed value draws no
 * swatch rather than a wrong one.
 */
export function roiDisplayColorCss(
    color: string | null | undefined
): string | undefined {
    if (!color) {
        return undefined;
    }
    const parts = color.split('\\').map((part) => part.trim());
    if (parts.length !== 3) {
        return undefined;
    }
    const channels = parts.map((part) =>
        /^\d{1,3}$/.test(part) ? Number(part) : NaN
    );
    if (channels.some((channel) => Number.isNaN(channel) || channel > 255)) {
        return undefined;
    }
    return `rgb(${channels.join(', ')})`;
}

/** Per-modality dataset counts (name-sorted) for the composition footer. */
export function seriesComposition(
    series: readonly SeriesDto[]
): Array<{ modality: string; count: number }> {
    const counts = new Map<string, number>();
    for (const s of series) {
        const modality = seriesModality(s);
        counts.set(modality, (counts.get(modality) ?? 0) + 1);
    }
    return [...counts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([modality, count]) => ({ modality, count }));
}
